import {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Button,
  Card,
  CardProps,
  Container,
  Divider,
  Grid,
  Menu,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// custom components
import { Iconify, Page } from '@/components';
// hooks
import useAuth from '@/hooks/useAuth';
import useSettings from '@/hooks/useSettings';
// routes
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { ROOT_INVOICES } from '@/features/invoices/routes/paths';
import { ROOTS_JOB_SCHEDULER } from '@/features/job-scheduler/routes/paths';
import { ROOTS_CLIENT_MANAGEMENT } from '@/features/users/clients/routes/paths';
// API
import { getDashboardData } from '../api';
// types
import {
  DashboardAttentionItem,
  DashboardBookingStatus,
  DashboardCleaner,
  DashboardData,
  DashboardDataQuery,
  DashboardNewClientsChartPoint,
  DashboardRangePreset,
  DashboardRevenueByService,
  DashboardRevenueChartPoint,
  DashboardTodaySession,
  DashboardTopLocation,
} from '../types';
import { fNumber } from '@/utils/formatNumber';

type QuickRangeSelection = DashboardRangePreset | 'today' | 'currentYear';

const RANGE_OPTIONS: { value: QuickRangeSelection; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'currentYear', label: 'Year to date' },
];

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});

const MONEY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const COMPACT_CURRENCY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  notation: 'compact',
  maximumFractionDigits: 1,
});

type RangeSelection = QuickRangeSelection | 'custom';

type PanelProps = CardProps & {
  children: ReactNode;
};

function Panel({ children, sx, ...other }: PanelProps) {
  return (
    <Card
      sx={{
        p: { xs: 2, md: 3 },
        height: '100%',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        ...sx,
      }}
      {...other}
    >
      {children}
    </Card>
  );
}

function toNumber(value: number | null | undefined) {
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function formatCurrency(value: number | null | undefined) {
  return CURRENCY_FORMATTER.format(toNumber(value));
}

function formatMoney(value: number | null | undefined) {
  return MONEY_FORMATTER.format(toNumber(value));
}

function formatCompactCurrency(value: number | null | undefined) {
  const amount = toNumber(value);

  if (Math.abs(amount) >= 1000) {
    return COMPACT_CURRENCY_FORMATTER.format(amount).replace('K', 'k');
  }

  return formatCurrency(amount);
}

function formatSignedNumber(value: number | null | undefined) {
  const amount = toNumber(value);
  return amount > 0 ? `+${fNumber(amount)}` : fNumber(amount);
}

function formatSignedPercent(value: number | null | undefined) {
  const amount = toNumber(value);
  const formatted = amount.toLocaleString('en-GB', {
    maximumFractionDigits: 1,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 1,
  });

  return `${amount > 0 ? '+' : ''}${formatted}%`;
}

function formatPercent(
  value: number | null | undefined,
  maximumFractionDigits = 1
) {
  const amount = toNumber(value);
  return `${amount.toLocaleString('en-GB', {
    maximumFractionDigits,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : maximumFractionDigits,
  })}%`;
}

function parseDate(date: string | null | undefined) {
  if (!date) {
    return null;
  }

  const parsed = parseISO(date);
  return isValid(parsed) ? parsed : null;
}

function formatRangeDate(date: string | null | undefined) {
  const parsed = parseDate(date);
  return parsed ? format(parsed, 'd MMM') : '';
}

function formatRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined
) {
  const start = formatRangeDate(startDate);
  const end = formatRangeDate(endDate);

  if (start && end) {
    return `${start} - ${end}`;
  }

  return 'Custom range';
}

function formatDateQueryValue(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

function getQuickRangeQuery(value: QuickRangeSelection): DashboardDataQuery {
  if (value === 'today') {
    const today = formatDateQueryValue(new Date());

    return {
      start_date: today,
      end_date: today,
    };
  }

  if (value === 'currentYear') {
    const today = new Date();
    const currentYear = today.getFullYear();

    return {
      start_date: formatDateQueryValue(new Date(currentYear, 0, 1)),
      end_date: formatDateQueryValue(today),
    };
  }

  return { range: value };
}

function toDateInputValue(date: string | null | undefined) {
  const parsed = parseDate(date);
  return parsed ? format(parsed, 'yyyy-MM-dd') : '';
}

function formatDuration(hours: number | null | undefined) {
  const totalMinutes = Math.round(toNumber(hours) * 60);
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!wholeHours) {
    return `${minutes}m`;
  }

  if (!minutes) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes}m`;
}

function getUserFirstName(user: ReturnType<typeof useAuth>['user']) {
  const firstName = user?.first_name;
  return typeof firstName === 'string' && firstName.trim()
    ? firstName
    : 'there';
}

function getTimeBasedGreeting(date = new Date()) {
  const hour = date.getHours();

  if (hour < 12) {
    return 'Good morning';
  }

  if (hour < 18) {
    return 'Good afternoon';
  }

  return 'Good evening';
}

function getMsUntilNextGreeting(date = new Date()) {
  const hour = date.getHours();
  const nextBoundary = new Date(date);

  if (hour < 12) {
    nextBoundary.setHours(12, 0, 0, 0);
  } else if (hour < 18) {
    nextBoundary.setHours(18, 0, 0, 0);
  } else {
    nextBoundary.setDate(nextBoundary.getDate() + 1);
    nextBoundary.setHours(0, 0, 0, 0);
  }

  return Math.max(nextBoundary.getTime() - date.getTime(), 1000);
}

function getStatusColor(keyOrLabel: string) {
  const normalized = keyOrLabel.toLowerCase();

  if (normalized.includes('complete') || normalized.includes('confirm')) {
    return '#1F9D57';
  }

  if (normalized.includes('progress')) {
    return '#7DC7A0';
  }

  if (normalized.includes('pending') || normalized.includes('unassigned')) {
    return '#F4B740';
  }

  if (normalized.includes('cancel')) {
    return '#CBD3CF';
  }

  if (normalized.includes('due') || normalized.includes('overdue')) {
    return '#D84D4D';
  }

  return '#1F9D57';
}

function TrendPill({
  value,
  label,
  featured = false,
}: {
  value: number;
  label?: string;
  featured?: boolean;
}) {
  const theme = useTheme();
  const isPositive = value >= 0;
  const color = isPositive
    ? theme.palette.success.dark
    : theme.palette.error.main;
  const featuredColor = isPositive ? '#9FE0B9' : '#FFB4B4';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: featured ? alpha('#FFFFFF', 0.1) : alpha(color, 0.12),
        color: featured ? featuredColor : color,
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
      }}
    >
      {label ?? formatSignedNumber(value)}
    </Box>
  );
}

function clampPercent(value: number) {
  return Math.min(100, Math.max(0, value));
}

type RevenueBilledBreakdownProps = {
  totalAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
};

function RevenueBilledBreakdown({
  totalAmount,
  collectedAmount,
  outstandingAmount,
}: RevenueBilledBreakdownProps) {
  const collected = Math.max(0, toNumber(collectedAmount));
  const outstanding = Math.max(0, toNumber(outstandingAmount));
  const total = Math.max(0, toNumber(totalAmount));
  const splitTotal = collected + outstanding;
  const denominator = splitTotal > 0 ? splitTotal : total;
  const collectedPercent = denominator
    ? clampPercent((collected / denominator) * 100)
    : 0;
  const collectedPercentRounded = Math.round(collectedPercent);
  const outstandingPercentRounded = denominator
    ? Math.max(0, 100 - collectedPercentRounded)
    : 0;

  return (
    <Stack spacing={2} sx={{ mt: 0.5 }}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        <Typography
          variant="body2"
          sx={{
            color: '#9FE0B9',
            fontSize: { xs: 15, md: 16 },
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Collected{' '}
          <Box component="span" sx={{ color: 'common.white', fontWeight: 900 }}>
            {formatCurrency(collected)}
          </Box>
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: '#9FE0B9',
            fontSize: { xs: 15, md: 16 },
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          Outstanding{' '}
          <Box component="span" sx={{ color: '#F6CA55', fontWeight: 900 }}>
            {formatCurrency(outstanding)}
          </Box>
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="body2"
            sx={{ color: '#9FE0B9', fontWeight: 800, whiteSpace: 'nowrap' }}
          >
            {formatPercent(collectedPercentRounded, 0)} collected
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#9FE0B9',
              fontWeight: 800,
              textAlign: 'right',
              whiteSpace: 'nowrap',
            }}
          >
            {formatPercent(outstandingPercentRounded, 0)} outstanding
          </Typography>
        </Stack>
        <Box
          aria-label={`${formatPercent(
            collectedPercentRounded,
            0
          )} collected, ${formatPercent(
            outstandingPercentRounded,
            0
          )} outstanding`}
          sx={{
            height: 12,
            overflow: 'hidden',
            borderRadius: 999,
            bgcolor: alpha('#C9B85F', 0.42),
          }}
        >
          <Box
            sx={{
              width: `${collectedPercent}%`,
              height: 1,
              borderRadius: 999,
              bgcolor: '#86D3A5',
            }}
          />
        </Box>
      </Stack>
    </Stack>
  );
}

type ProfitBreakdownProps = {
  revenueAmount: number;
  providerPaymentsAmount: number;
};

function ProfitBreakdown({
  revenueAmount,
  providerPaymentsAmount,
}: ProfitBreakdownProps) {
  return (
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        lineHeight: 1.5,
      }}
    >
      Revenue{' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 900 }}>
        {formatCurrency(revenueAmount)}
      </Box>{' '}
      - Provider payments{' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 900 }}>
        {formatCurrency(providerPaymentsAmount)}
      </Box>
    </Typography>
  );
}

type SummaryCardProps = {
  title: string;
  value: string;
  helper?: ReactNode;
  trend?: ReactNode;
  comparisonLabel?: string;
  subtext?: string;
  featured?: boolean;
};

function SummaryCard({
  title,
  value,
  helper,
  trend,
  comparisonLabel,
  subtext,
  featured = false,
}: SummaryCardProps) {
  return (
    <Panel
      sx={{
        minHeight: featured ? 248 : 184,
        bgcolor: featured ? '#124A2D' : 'background.paper',
        color: featured ? 'common.white' : 'text.primary',
      }}
    >
      <Stack spacing={1.5} sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography
            variant="overline"
            sx={{
              color: featured ? '#9FE0B9' : 'text.secondary',
              lineHeight: 1.2,
              fontWeight: 800,
            }}
          >
            {title}
          </Typography>
          {(trend || comparisonLabel) && (
            <Stack spacing={0.5} alignItems="flex-end">
              {trend}
              {comparisonLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: featured ? alpha('#FFFFFF', 0.62) : 'text.secondary',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textAlign: 'right',
                  }}
                >
                  {comparisonLabel}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: featured ? { xs: 34, md: 40 } : { xs: 30, md: 34 },
            lineHeight: 1,
            fontWeight: 900,
            letterSpacing: 0,
          }}
        >
          {value}
        </Typography>
        {helper}
        {subtext && (
          <Typography
            variant="body2"
            sx={{ color: featured ? alpha('#FFFFFF', 0.68) : 'text.secondary' }}
          >
            {subtext}
          </Typography>
        )}
      </Stack>
    </Panel>
  );
}

function ChartLegend({
  currentRangeLabel,
  previousRangeLabel,
}: {
  currentRangeLabel: string;
  previousRangeLabel: string;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '3px',
            bgcolor: '#20A160',
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {currentRangeLabel}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '3px',
            bgcolor: '#D8E2DC',
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {previousRangeLabel}
        </Typography>
      </Stack>
    </Stack>
  );
}

function RevenueComparisonChart({
  data,
  currentRangeLabel,
  previousRangeLabel,
}: {
  data: DashboardRevenueChartPoint[];
  currentRangeLabel: string;
  previousRangeLabel: string;
}) {
  const maxValue = Math.max(
    ...data.flatMap((point) => [
      toNumber(point.amount),
      toNumber(point.previousAmount),
    ]),
    1
  );

  return (
    <Panel>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
        >
          <Typography variant="h6">
            Revenue - {currentRangeLabel} vs {previousRangeLabel}
          </Typography>
          <ChartLegend
            currentRangeLabel={currentRangeLabel}
            previousRangeLabel={previousRangeLabel}
          />
        </Stack>
        {data.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${data.length}, minmax(76px, 1fr))`,
              gap: { xs: 2, md: 5 },
              alignItems: 'end',
              minHeight: 214,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            {data.map((point) => (
              <Stack
                key={`${point.label}-${point.startDate}`}
                spacing={1}
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#16824A', fontWeight: 900 }}
                >
                  {formatCompactCurrency(point.amount)}
                </Typography>
                <Tooltip
                  arrow
                  title={
                    <Stack spacing={0.5}>
                      <Typography variant="caption">
                        Current: {point.label}: {formatCurrency(point.amount)}
                      </Typography>
                      <Typography variant="caption">
                        Previous: {point.previousLabel}:{' '}
                        {formatCurrency(point.previousAmount)}
                      </Typography>
                    </Stack>
                  }
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-end"
                    sx={{ height: 142 }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: `${Math.max(
                          10,
                          (toNumber(point.previousAmount) / maxValue) * 142
                        )}px`,
                        borderRadius: '6px 6px 0 0',
                        bgcolor: '#D8E2DC',
                      }}
                    />
                    <Box
                      sx={{
                        width: 28,
                        height: `${Math.max(
                          10,
                          (toNumber(point.amount) / maxValue) * 142
                        )}px`,
                        borderRadius: '6px 6px 0 0',
                        bgcolor: '#20A160',
                      }}
                    />
                  </Stack>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 700 }}
                >
                  {point.label}
                </Typography>
              </Stack>
            ))}
          </Box>
        ) : (
          <EmptyPanelState minHeight={214} />
        )}
      </Stack>
    </Panel>
  );
}

function NewClientsChart({
  data,
  total,
  delta,
  comparisonLabel,
}: {
  data: DashboardNewClientsChartPoint[];
  total: number;
  delta: number;
  comparisonLabel: string;
}) {
  const maxValue = Math.max(...data.map((point) => toNumber(point.count)), 1);

  return (
    <Panel>
      <Stack spacing={3} sx={{ height: '100%' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
        >
          <Typography variant="h6">New clients</Typography>
          <Typography variant="body2" color="text.secondary">
            {fNumber(total)} this period - {formatSignedNumber(delta)}{' '}
            {comparisonLabel}
          </Typography>
        </Stack>
        {data.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${data.length}, minmax(76px, 1fr))`,
              gap: { xs: 2, md: 6 },
              alignItems: 'end',
              minHeight: 214,
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              overflowX: 'auto',
            }}
          >
            {data.map((point) => (
              <Stack
                key={`${point.label}-${point.startDate}`}
                spacing={1}
                alignItems="center"
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  {fNumber(point.count)}
                </Typography>
                <Box
                  sx={{
                    width: 38,
                    height: `${Math.max(
                      18,
                      (toNumber(point.count) / maxValue) * 142
                    )}px`,
                    borderRadius: '6px 6px 0 0',
                    bgcolor: '#20A160',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 700 }}
                >
                  {point.label}
                </Typography>
              </Stack>
            ))}
          </Box>
        ) : (
          <EmptyPanelState minHeight={214} />
        )}
      </Stack>
    </Panel>
  );
}

function EmptyPanelState({ minHeight = 120 }: { minHeight?: number }) {
  return (
    <Box
      sx={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2">No data available</Typography>
    </Box>
  );
}

function StatusPill({ label }: { label: string }) {
  const color = getStatusColor(label);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.25,
        py: 0.45,
        borderRadius: 999,
        bgcolor: alpha(color, 0.12),
        color,
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

function TodaySessionsCard({
  total,
  items,
}: {
  total: number;
  items: DashboardTodaySession[];
}) {
  return (
    <Panel sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: { xs: 2, md: 3 }, py: 2 }}
      >
        <Typography variant="h6">Today's sessions</Typography>
        <Button
          component={RouterLink}
          to={bookingsManagementPath('sessionsList')}
          size="small"
          endIcon={<Iconify icon="eva:arrow-forward-fill" />}
          sx={{ color: '#16824A', fontWeight: 800 }}
        >
          View all {fNumber(total)}
        </Button>
      </Stack>
      <Divider />
      {items.length ? (
        <Stack divider={<Divider flexItem />}>
          {items.map((session) => (
            <Stack
              key={String(session.sessionId)}
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}
            >
              <Typography sx={{ width: 52, fontWeight: 900 }}>
                {session.time}
              </Typography>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 900 }}>
                  {session.clientLabel}
                </Typography>
                <Typography noWrap variant="body2" color="text.secondary">
                  {[session.serviceLabel, session.outwardCode]
                    .filter(Boolean)
                    .join(' - ')}
                </Typography>
              </Box>
              <StatusPill
                label={session.attentionLabel || session.statusLabel}
              />
            </Stack>
          ))}
        </Stack>
      ) : (
        <EmptyPanelState minHeight={248} />
      )}
    </Panel>
  );
}

function BookingStatusRow({ status }: { status: DashboardBookingStatus }) {
  const color = getStatusColor(status.key || status.label);

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography
        variant="body2"
        sx={{ width: 106, fontWeight: 700, color: 'text.secondary' }}
      >
        {status.label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 80 }}>
        <Box
          sx={{
            height: 24,
            borderRadius: 1,
            bgcolor: '#EEF2EF',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${Math.min(
                100,
                Math.max(status.count > 0 ? 4 : 0, toNumber(status.percent))
              )}%`,
              height: '100%',
              borderRadius: 1,
              bgcolor: color,
            }}
          />
        </Box>
      </Box>
      <Typography
        variant="subtitle2"
        sx={{ width: 44, textAlign: 'right', fontWeight: 900 }}
      >
        {fNumber(status.count)}
      </Typography>
    </Stack>
  );
}

function RevenueByServiceBar({ data }: { data: DashboardRevenueByService[] }) {
  const colors = ['#20A160', '#82CBA2', '#D4EADE', '#F4B740', '#CBD3CF'];

  if (!data.length) {
    return <EmptyPanelState minHeight={82} />;
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" color="text.secondary">
        Revenue by service
      </Typography>
      <Box
        sx={{
          display: 'flex',
          height: 28,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: '#EEF2EF',
        }}
      >
        {data.map((service, index) => (
          <Tooltip
            key={service.serviceRef || service.serviceName}
            title={`${service.serviceName}: ${formatMoney(service.amount)}`}
          >
            <Box
              sx={{
                width: `${Math.max(
                  toNumber(service.percent),
                  service.amount > 0 ? 2 : 0
                )}%`,
                bgcolor: colors[index % colors.length],
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        {data.map((service, index) => (
          <Stack
            key={service.serviceRef || service.serviceName}
            direction="row"
            spacing={0.75}
            alignItems="center"
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '3px',
                bgcolor: colors[index % colors.length],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {service.serviceName} {formatMoney(service.amount)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function BookingsCard({
  total,
  statuses,
  revenueByService,
}: {
  total: number;
  statuses: DashboardBookingStatus[];
  revenueByService: DashboardRevenueByService[];
}) {
  return (
    <Panel>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Typography variant="h6">
            Bookings - {fNumber(total)} this period
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {revenueByService
              .slice(0, 3)
              .map(
                (service) =>
                  `${formatPercent(
                    service.percent,
                    0
                  )} ${service.serviceName.toLowerCase()}`
              )
              .join(' - ')}
          </Typography>
        </Stack>
        {statuses.length ? (
          <Stack spacing={2}>
            {statuses.map((status) => (
              <BookingStatusRow key={status.key} status={status} />
            ))}
          </Stack>
        ) : (
          <EmptyPanelState />
        )}
        <RevenueByServiceBar data={revenueByService} />
      </Stack>
    </Panel>
  );
}

const ATTENTION_CONFIG: Record<
  string,
  {
    title: string;
    action: string;
    route: string;
    color: string;
    getDetail: (item: DashboardAttentionItem) => string;
  }
> = {
  overdueInvoices: {
    title: 'Overdue invoices',
    action: 'Chase now',
    route: ROOT_INVOICES,
    color: '#D84D4D',
    getDetail: (item) =>
      `${formatCurrency(item.amount)} outstanding${
        item.oldestAgeDays
          ? ` - oldest ${fNumber(item.oldestAgeDays)} days`
          : ''
      }`,
  },
  missingPaymentMethods: {
    title: 'Missing payment methods',
    action: 'Send links',
    route: ROOTS_CLIENT_MANAGEMENT,
    color: '#B76A00',
    getDetail: () => "Customers can't be charged after their next session",
  },
  unassignedSessions: {
    title: 'Unassigned sessions',
    action: 'Assign',
    route: ROOTS_JOB_SCHEDULER,
    color: '#B76A00',
    getDetail: (item) =>
      `Within the next ${fNumber(
        item.windowHours ?? 48
      )} hours - assign a cleaner`,
  },
};

function AttentionCard({ items }: { items: DashboardAttentionItem[] }) {
  return (
    <Panel>
      <Stack spacing={2}>
        <Typography variant="h6">Needs your attention</Typography>
        {items.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: `repeat(${Math.min(items.length, 3)}, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {items.map((item) => {
              const config = ATTENTION_CONFIG[item.key] ?? {
                title: item.key,
                action: 'Review',
                route: bookingsManagementPath('sessionsList'),
                color: '#B76A00',
                getDetail: () => '',
              };

              return (
                <Box
                  key={item.key}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha(config.color, 0.28),
                    bgcolor: alpha(config.color, 0.06),
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: config.color }}>
                        {config.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {config.getDetail(item)}
                      </Typography>
                      <Button
                        component={RouterLink}
                        to={config.route}
                        size="small"
                        endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                        sx={{
                          px: 0,
                          mt: 0.5,
                          color: config.color,
                          fontWeight: 900,
                        }}
                      >
                        {config.action}
                      </Button>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ color: config.color, fontWeight: 900 }}
                    >
                      {fNumber(item.count)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        ) : (
          <EmptyPanelState minHeight={96} />
        )}
      </Stack>
    </Panel>
  );
}

function TopLocationsCard({
  total,
  remaining,
  items,
}: {
  total: number;
  remaining: number;
  items: DashboardTopLocation[];
}) {
  return (
    <Panel>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="h6">Top booking locations</Typography>
          {remaining > 0 && (
            <Typography variant="body2" color="text.secondary">
              +{fNumber(remaining)}
            </Typography>
          )}
        </Stack>
        {items.length ? (
          <Stack spacing={1.25}>
            {items.map((location) => (
              <Stack
                key={location.outwardCode}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <Tooltip title={location.townOrCity || location.outwardCode}>
                  <Typography
                    variant="subtitle2"
                    sx={{ width: 58, fontWeight: 900 }}
                  >
                    {location.outwardCode}
                  </Typography>
                </Tooltip>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 99,
                      bgcolor: '#EEF2EF',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.min(
                          100,
                          Math.max(4, toNumber(location.percent))
                        )}%`,
                        borderRadius: 99,
                        bgcolor: '#20A160',
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{ width: 30, textAlign: 'right', fontWeight: 900 }}
                >
                  {fNumber(location.count)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <EmptyPanelState />
        )}
        {total > 0 && (
          <Typography variant="caption" color="text.secondary">
            {fNumber(total)} locations this period
          </Typography>
        )}
      </Stack>
    </Panel>
  );
}

function OperationsCard({ data }: { data: DashboardData['operations'] }) {
  const rows = [
    { label: 'Avg. job value', value: formatMoney(data.avgJobValue) },
    { label: 'Avg. duration', value: formatDuration(data.avgDurationHours) },
    { label: 'Cancellation rate', value: formatPercent(data.cancellationRate) },
    {
      label: 'Coupons used',
      value: `${fNumber(data.couponsUsed)} - ${formatMoney(
        data.couponImpactAmount
      )} impact`,
    },
  ];

  return (
    <Panel sx={{ p: 0 }}>
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ p: { xs: 2, md: 3 }, pb: 2 }}>
          <Typography variant="h6">Operations</Typography>
        </Box>
        <Divider />
        <Stack divider={<Divider flexItem />}>
          {rows.map((row) => (
            <Stack
              key={row.label}
              direction="row"
              justifyContent="space-between"
              spacing={2}
              sx={{ px: { xs: 2, md: 3 }, py: 1.25 }}
            >
              <Typography color="text.secondary">{row.label}</Typography>
              <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Panel>
  );
}

function CleanersCard({
  totalActive,
  remaining,
  items,
}: {
  totalActive: number;
  remaining: number;
  items: DashboardCleaner[];
}) {
  return (
    <Panel sx={{ p: 0 }}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          sx={{ p: { xs: 2, md: 3 }, pb: 2 }}
        >
          <Typography variant="h6">Cleaners</Typography>
          <Typography variant="body2" color="text.secondary">
            {fNumber(totalActive)} active
          </Typography>
        </Stack>
        <Divider />
        {items.length ? (
          <Stack divider={<Divider flexItem />}>
            {items.map((cleaner) => (
              <Stack
                key={cleaner.displayName}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ px: { xs: 2, md: 3 }, py: 1.25 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: '#DDF4E7',
                    color: '#16824A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 900,
                    flexShrink: 0,
                  }}
                >
                  {cleaner.initials}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 900 }}>
                    {cleaner.displayName}
                  </Typography>
                  <Typography noWrap variant="caption" color="text.secondary">
                    {fNumber(cleaner.jobsCompleted)} jobs - rating{' '}
                    {cleaner.avgRating != null
                      ? cleaner.avgRating.toFixed(1)
                      : 'N/A'}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#16824A', fontWeight: 900 }}>
                  {formatPercent(cleaner.completionRate, 0)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <EmptyPanelState minHeight={192} />
        )}
        {remaining > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: { xs: 2, md: 3 }, py: 1 }}
          >
            +{fNumber(remaining)} more active cleaners
          </Typography>
        )}
      </Stack>
    </Panel>
  );
}

function DashboardSkeleton() {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Skeleton height={50} />
      </Grid>
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Skeleton
            variant="rectangular"
            height={index === 0 ? 248 : 184}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      ))}
      {[...Array(5)].map((_, index) => (
        <Grid item xs={12} md={index < 2 ? 6 : 4} key={`panel-${index}`}>
          <Skeleton
            variant="rectangular"
            height={280}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export default function AgentDashboard() {
  const { user } = useAuth();
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [dashboardData, setDashboardData] = useState<DashboardData>();
  const [query, setQuery] = useState<DashboardDataQuery>({ range: '30d' });
  const [rangeSelection, setRangeSelection] = useState<RangeSelection>('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [rangeMenuAnchor, setRangeMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [timeBasedGreeting, setTimeBasedGreeting] = useState(() =>
    getTimeBasedGreeting()
  );

  const fetchDashboardData = useCallback(
    async (nextQuery: DashboardDataQuery) => {
      setIsLoading(true);

      try {
        const responseData = await getDashboardData(nextQuery);
        setDashboardData(responseData);
        setDraftStartDate(toDateInputValue(responseData.meta.range.startDate));
        setDraftEndDate(toDateInputValue(responseData.meta.range.endDate));
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Error fetching dashboard data', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchDashboardData(query);
  }, [fetchDashboardData, query]);

  useEffect(() => {
    let timeoutId: number;

    const scheduleNextGreeting = () => {
      timeoutId = window.setTimeout(() => {
        setTimeBasedGreeting(getTimeBasedGreeting());
        scheduleNextGreeting();
      }, getMsUntilNextGreeting());
    };

    scheduleNextGreeting();

    return () => window.clearTimeout(timeoutId);
  }, []);

  const firstName = getUserFirstName(user);
  const todayLabel = useMemo(() => format(new Date(), 'EEEE d MMMM'), []);

  const rangeLabel = dashboardData
    ? dashboardData.meta.range.label ||
      formatRange(
        dashboardData.meta.range.startDate,
        dashboardData.meta.range.endDate
      )
    : 'Custom range';
  const previousRangeLabel = dashboardData
    ? dashboardData.meta.previousRange.label ||
      formatRange(
        dashboardData.meta.previousRange.startDate,
        dashboardData.meta.previousRange.endDate
      )
    : '';
  const comparisonLabel =
    dashboardData?.meta.previousRange.comparisonLabel ||
    (previousRangeLabel ? `vs ${previousRangeLabel}` : '');
  const showLostClientsKpi =
    dashboardData?.summary.lostClients.isRelevant === true;
  const secondaryKpiGridSize = showLostClientsKpi ? 4 : 6;

  const handlePresetChange = (
    _event: MouseEvent<HTMLElement>,
    value: QuickRangeSelection | null
  ) => {
    if (!value) {
      return;
    }

    setRangeSelection(value);
    setQuery(getQuickRangeQuery(value));
  };

  const handleOpenRangeMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setRangeMenuAnchor(event.currentTarget);
  };

  const handleCloseRangeMenu = () => {
    setRangeMenuAnchor(null);
  };

  const handleDraftStartDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraftStartDate(event.target.value);
  };

  const handleDraftEndDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDraftEndDate(event.target.value);
  };

  const handleApplyCustomRange = () => {
    if (!draftStartDate || !draftEndDate) {
      enqueueSnackbar('Choose a start and end date', { variant: 'warning' });
      return;
    }

    if (draftStartDate > draftEndDate) {
      enqueueSnackbar('Start date must be before end date', {
        variant: 'warning',
      });
      return;
    }

    setRangeSelection('custom');
    setQuery({ start_date: draftStartDate, end_date: draftEndDate });
    handleCloseRangeMenu();
  };

  const handleExport = () => {
    if (!dashboardData) {
      return;
    }

    const blob = new Blob([JSON.stringify(dashboardData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `dashboard-${toDateInputValue(
      dashboardData.meta.range.startDate
    )}-${toDateInputValue(dashboardData.meta.range.endDate)}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Page
      title="Dashboard"
      sx={{
        minHeight: '100%',
        bgcolor: alpha(theme.palette.grey[500], 0.08),
        py: { xs: 2, md: 3 },
      }}
    >
      <Container maxWidth={themeStretch ? false : 'xl'}>
        {isLoading && !dashboardData ? (
          <DashboardSkeleton />
        ) : dashboardData ? (
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 24, md: 28 },
                    lineHeight: 1.15,
                    fontWeight: 900,
                    letterSpacing: 0,
                  }}
                >
                  {timeBasedGreeting}, {firstName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {todayLabel} - {fNumber(dashboardData.todaySessions.total)}{' '}
                  sessions scheduled today
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={rangeSelection === 'custom' ? null : rangeSelection}
                  onChange={handlePresetChange}
                  sx={{
                    bgcolor: '#E9EFEB',
                    borderRadius: 1,
                    flexWrap: 'wrap',
                    maxWidth: { xs: '100%', sm: 'none' },
                    p: 0.25,
                    '& .MuiToggleButton-root': {
                      flex: { xs: '1 1 auto', sm: '0 0 auto' },
                      minWidth: 0,
                      px: { xs: 1.25, md: 2 },
                      py: 0.8,
                      border: 0,
                      borderRadius: 0.75,
                      color: 'text.secondary',
                      fontWeight: 800,
                      whiteSpace: 'nowrap',
                      '&.Mui-selected': {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: '0 1px 4px rgba(16, 24, 40, 0.08)',
                      },
                    },
                  }}
                >
                  {RANGE_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleOpenRangeMenu}
                  endIcon={<Iconify icon="eva:chevron-down-fill" />}
                  sx={{
                    bgcolor:
                      rangeSelection === 'custom'
                        ? 'background.paper'
                        : 'transparent',
                    borderColor: 'divider',
                    fontWeight: 800,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {rangeLabel}
                </Button>
                <Menu
                  anchorEl={rangeMenuAnchor}
                  open={Boolean(rangeMenuAnchor)}
                  onClose={handleCloseRangeMenu}
                  PaperProps={{ sx: { p: 2, width: 280, borderRadius: 1 } }}
                >
                  <Stack spacing={1.5}>
                    <TextField
                      label="Start"
                      type="date"
                      size="small"
                      value={draftStartDate}
                      onChange={handleDraftStartDateChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End"
                      type="date"
                      size="small"
                      value={draftEndDate}
                      onChange={handleDraftEndDateChange}
                      InputLabelProps={{ shrink: true }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        color="inherit"
                        onClick={handleCloseRangeMenu}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleApplyCustomRange}
                      >
                        Apply
                      </Button>
                    </Stack>
                  </Stack>
                </Menu>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleExport}
                  disabled={!dashboardData}
                  startIcon={<Iconify icon="eva:download-outline" />}
                  sx={{ borderColor: 'divider', fontWeight: 800 }}
                >
                  Export
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SummaryCard
                  featured
                  title="Revenue billed"
                  value={formatCurrency(
                    dashboardData.summary.revenueBilled.amount
                  )}
                  trend={
                    <TrendPill
                      value={dashboardData.summary.revenueBilled.changePercent}
                      label={formatSignedPercent(
                        dashboardData.summary.revenueBilled.changePercent
                      )}
                      featured
                    />
                  }
                  comparisonLabel={comparisonLabel}
                  helper={
                    <RevenueBilledBreakdown
                      totalAmount={dashboardData.summary.revenueBilled.amount}
                      collectedAmount={
                        dashboardData.summary.revenueBilled.collectedAmount
                      }
                      outstandingAmount={
                        dashboardData.summary.revenueBilled.outstandingAmount
                      }
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SummaryCard
                  title="Provider payments"
                  value={formatCurrency(
                    dashboardData.summary.providerPayments.amount
                  )}
                  trend={
                    <TrendPill
                      value={
                        dashboardData.summary.providerPayments.changePercent
                      }
                      label={formatSignedPercent(
                        dashboardData.summary.providerPayments.changePercent
                      )}
                    />
                  }
                  comparisonLabel={comparisonLabel}
                  subtext={`${formatCurrency(
                    dashboardData.summary.providerPayments.previousAmount
                  )} in ${previousRangeLabel}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <SummaryCard
                  title="Profit"
                  value={formatCurrency(dashboardData.summary.profit.amount)}
                  trend={
                    <TrendPill
                      value={dashboardData.summary.profit.changePercent}
                      label={formatSignedPercent(
                        dashboardData.summary.profit.changePercent
                      )}
                    />
                  }
                  comparisonLabel={comparisonLabel}
                  helper={
                    <ProfitBreakdown
                      revenueAmount={dashboardData.summary.profit.revenueAmount}
                      providerPaymentsAmount={
                        dashboardData.summary.profit.providerPaymentsAmount
                      }
                    />
                  }
                  subtext={`${formatCurrency(
                    dashboardData.summary.profit.previousAmount
                  )} in ${previousRangeLabel}`}
                />
              </Grid>

              <Grid item xs={12} sm={secondaryKpiGridSize}>
                <SummaryCard
                  title="New clients"
                  value={fNumber(dashboardData.summary.newClients.count)}
                  trend={
                    <TrendPill value={dashboardData.summary.newClients.delta} />
                  }
                  comparisonLabel={comparisonLabel}
                  subtext={`${fNumber(
                    dashboardData.summary.newClients.previousCount
                  )} in ${previousRangeLabel}`}
                />
              </Grid>
              {showLostClientsKpi && (
                <Grid item xs={12} sm={4}>
                  <SummaryCard
                    title="Lost clients"
                    value={fNumber(dashboardData.summary.lostClients.count)}
                    trend={
                      <TrendPill
                        value={dashboardData.summary.lostClients.delta}
                      />
                    }
                    comparisonLabel={comparisonLabel}
                    subtext={`${fNumber(
                      dashboardData.summary.lostClients.previousCount
                    )} in ${previousRangeLabel}`}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={secondaryKpiGridSize}>
                <SummaryCard
                  title="Sessions"
                  value={fNumber(dashboardData.summary.sessions.count)}
                  trend={
                    <TrendPill value={dashboardData.summary.sessions.delta} />
                  }
                  comparisonLabel={comparisonLabel}
                  subtext={`avg job value ${formatMoney(
                    dashboardData.summary.sessions.avgJobValue
                  )}`}
                />
              </Grid>

              <Grid item xs={12} md={7}>
                <RevenueComparisonChart
                  data={dashboardData.charts.revenue}
                  currentRangeLabel={rangeLabel}
                  previousRangeLabel={previousRangeLabel}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <NewClientsChart
                  data={dashboardData.charts.newClients}
                  total={dashboardData.summary.newClients.count}
                  delta={dashboardData.summary.newClients.delta}
                  comparisonLabel={comparisonLabel}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <TodaySessionsCard
                  total={dashboardData.todaySessions.total}
                  items={dashboardData.todaySessions.items}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <BookingsCard
                  total={dashboardData.summary.sessions.count}
                  statuses={dashboardData.charts.bookingStatuses}
                  revenueByService={dashboardData.charts.revenueByService}
                />
              </Grid>

              <Grid item xs={12}>
                <AttentionCard items={dashboardData.attention} />
              </Grid>

              <Grid item xs={12} md={4}>
                <TopLocationsCard
                  total={dashboardData.topLocations.total}
                  remaining={dashboardData.topLocations.remaining}
                  items={dashboardData.topLocations.items}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <OperationsCard data={dashboardData.operations} />
              </Grid>
              <Grid item xs={12} md={4}>
                <CleanersCard
                  totalActive={dashboardData.cleaners.totalActive}
                  remaining={dashboardData.cleaners.remaining}
                  items={dashboardData.cleaners.items}
                />
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <EmptyPanelState minHeight={320} />
        )}
      </Container>
    </Page>
  );
}
